import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AddVariationDialog } from "@/components/catalog/AddVariationDialog";
import { NewDesignDialog } from "@/components/catalog/NewDesignDialog";
import { toast } from "sonner";
import { colorOptionLabel } from "@/lib/naming";

const KIND = { EXISTING_COLLECTION: "Mosaic Collection", NEW_COLLECTION: "New Mosaic Collection", MIX: "MIX — identify SKU", UNKNOWN: "Unknown SKU" };
const btn = "h-8 px-3 text-xs";

// ID-072: resolve the originating row with the exact SKU the server returned (no guessing, no silent substitution)
const skuSummary = async (sku) => (await api.post("/containers/import-v51/sku-check", { sku })).data.match;
const confirmMatch = (item, m, created) => toast.success(`${created ? `${m.sku} created · ` : ""}Row ${item.row} matched to ${m.sku} · ${item.boxes} boxes`);

const ColorMatcher = ({ item, design, colors, onUse, onDesignsChanged }) => {
  const [picked, setPicked] = useState(item.colors || []);
  const [result, setResult] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const lo = design?.min_colors || 1;
  const hi = design?.max_colors || 8;
  const find = async () => {
    try {
      const { data } = await api.post("/containers/import-v51/sku-check", { design_id: design.design_id, colors: picked });
      setResult(data);
      if (data.match) { onUse(data.match); confirmMatch(item, data.match, false); }
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const t = `resolve-${item.row}`;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5" data-testid={`${t}-colors`}>
        {picked.map((c) => (
          <button key={c} type="button" onClick={() => { setPicked((p) => p.filter((x) => x !== c)); setResult(null); }} className="rounded-full border border-terracotta bg-sand px-2 py-0.5 font-mono text-xs" data-testid={`${t}-color-${c}`}>{colorOptionLabel(colors.find((x) => x.color_code === c) || { color_code: c })} ×</button>
        ))}
        {picked.length < hi && (
          <select value="" onChange={(e) => { setPicked((p) => [...p, e.target.value]); setResult(null); }} className="h-8 rounded-md border border-border bg-white px-2 text-xs" data-testid={`${t}-add-color`}>
            <option value="">+ color…</option>
            {colors.filter((c) => !picked.includes(c.color_code)).map((c) => <option key={c.color_code} value={c.color_code} data-testid={`${t}-option-${c.color_code}`}>{colorOptionLabel(c)}</option>)}
          </select>
        )}
        <Button variant="outline" className={btn} disabled={picked.length < lo} onClick={find} data-testid={`${t}-find`}>Find variation</Button>
      </div>
      {result && (result.match ? (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`${t}-match`}>
          Matches <span className="font-mono">{result.match.sku}</span> ({result.match.colors.join(" + ")}) · {result.match.status === "INACTIVE" ? "inactive — stays hidden from dealers" : result.match.status}
          <Button className={`${btn} bg-charcoal text-white`} onClick={() => onUse(result.match)} data-testid={`${t}-use`}>Use {result.match.sku}</Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`${t}-nomatch`}>
          No {design.name} variation has exactly these colors.
          <Button variant="outline" className={btn} onClick={() => setAddOpen(true)} data-testid={`${t}-add-variation`}>+ Variation</Button>
        </div>
      ))}
      <AddVariationDialog design={design} colors={colors} open={addOpen} onOpenChange={setAddOpen} initialColors={picked} fullNames
        onCreated={async (data) => {
          await onDesignsChanged();
          if (data?.skus?.length !== 1) { toast.error("Variation created, but the server did not return exactly one SKU — find it by colors"); return; }
          try { const m = await skuSummary(data.skus[0]); onUse(m); confirmMatch(item, m, true); }
          catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        }} />
    </div>
  );
};

const SkuEntry = ({ item, onUse }) => {
  const [sku, setSku] = useState("");
  const go = async () => {
    try { const { data } = await api.post("/containers/import-v51/sku-check", { sku }); onUse(data.match); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input value={sku} onChange={(e) => setSku(e.target.value.toUpperCase())} placeholder="Catalog SKU" className="h-8 w-32 bg-white font-mono text-xs" data-testid={`resolve-${item.row}-sku-input`} />
      <Button variant="outline" className={btn} disabled={!sku.trim()} onClick={go} data-testid={`resolve-${item.row}-sku-check`}>Use SKU</Button>
    </div>
  );
};

export const ResolveRow = ({ item, value, onChange, colors, designs, onDesignsChanged, previewId }) => {
  const [designId, setDesignId] = useState(item.design_id);
  const [newOpen, setNewOpen] = useState(false);
  const [choices, setChoices] = useState(null);
  const design = designs.find((d) => d.design_id === designId);
  const use = (m) => { setChoices(null); onChange({ sku: m.sku, label: m.label, status: m.status }); };
  const t = `resolve-${item.row}`;
  const created = async (d) => {
    await onDesignsChanged();
    setDesignId(d.design_id);
    const skus = d.skus || [];
    try {
      const ms = await Promise.all(skus.map(skuSummary));
      if (ms.length === 1) { use(ms[0]); confirmMatch(item, ms[0], true); }
      else { setChoices(ms); toast.info(`${skus.length} variations created — choose the one for row ${item.row}`); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <li className="space-y-2 rounded-md border border-border bg-white p-3" data-testid={t}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-mono text-charcoal">Row {item.row}</span>
        {item.pallet && <span className="text-warmgrey">Pallet {item.pallet}</span>}
        <span className="font-mono">{item.raw_sku || "—"}</span>
        {item.description && <span className="text-charcoal">{item.description}</span>}
        <strong>{item.boxes} boxes</strong>
        <span className="badge-status badge-neutral" data-testid={`${t}-kind`}>{KIND[item.kind]}{design && item.kind !== "EXISTING_COLLECTION" ? ` → ${design.name}` : ""}</span>
      </div>
      {value ? (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`${t}-resolved`}>
          {value.exclude ? <span className="text-terracotta">Excluded from import — {item.boxes} boxes</span>
            : <span>→ <span className="font-mono">{value.sku}</span> · {value.label}{value.status === "INACTIVE" ? " · inactive" : ""} · Row {item.row} matched · {item.boxes} boxes</span>}
          <Button variant="outline" className={btn} onClick={() => onChange(null)} data-testid={`${t}-change`}>Change</Button>
        </div>
      ) : (
        <>
          <p className="text-xs text-warmgrey">{designId && designId !== item.design_id ? `${design?.name || designId} now exists — ${choices ? "choose the variation on this line" : "select the colors on this line to match its variation"}` : item.note}</p>
          {choices ? (
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`${t}-choose`}>
              Which new variation is on this line?
              {choices.map((m) => (
                <Button key={m.sku} variant="outline" className={btn} onClick={() => { use(m); confirmMatch(item, m, false); }} data-testid={`${t}-choose-${m.sku}`}>{m.sku} · {m.colors.join(" + ")}</Button>
              ))}
            </div>
          ) : design ? <ColorMatcher item={item} design={design} colors={colors} onUse={use} onDesignsChanged={onDesignsChanged} />
            : item.kind === "NEW_COLLECTION" ? (
              <Button variant="outline" className={btn} onClick={() => setNewOpen(true)} data-testid={`${t}-new-design`}>+ New Mosaic Design ({item.prefill?.name})</Button>
            ) : <SkuEntry item={item} onUse={use} />}
          <Button variant="ghost" className={`${btn} text-terracotta`} onClick={() => onChange({ exclude: true })} data-testid={`${t}-exclude`}>Exclude from this import</Button>
        </>
      )}
      <NewDesignDialog open={newOpen} onOpenChange={setNewOpen} prefill={item.prefill} draftKey={previewId ? `zc-import-design-${previewId}-${item.row}` : undefined}
        onCreated={created} />
    </li>
  );
};
